import React, { createContext, useContext, useState, useCallback } from "react";
import { Lock, X } from "lucide-react";
import { supabase } from "./supabase";
import { toast } from "sonner";

// REMINDER: DO NOT HARDCODE THE URL - use window.location.origin

const LoginPromptContext = createContext({ requireLogin: () => {} });

export const useLoginPrompt = () => useContext(LoginPromptContext);

export const LoginPromptProvider = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const requireLogin = useCallback((reasonText = "Login to continue") => {
    setReason(reasonText);
    setOpen(true);
  }, []);

  const close = () => { if (!busy) setOpen(false); };

  const handleGoogle = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          queryParams: { access_type: "offline", prompt: "select_account" },
        },
      });
      if (error) throw error;
    } catch (err) {
      toast.error(err?.message || "Google sign-in failed");
      setBusy(false);
    }
  };

  const handleEmailRoute = () => {
    setOpen(false);
    window.location.href = "/login";
  };

  return (
    <LoginPromptContext.Provider value={{ requireLogin }}>
      {children}
      {open && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="login-required-modal">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={close}
          />
          <div className="relative w-full max-w-sm bg-card border border-border rounded-2xl shadow-2xl p-7 animate-in zoom-in-95 duration-200">
            <button
              onClick={close}
              data-testid="login-modal-close"
              className="absolute top-3 right-3 p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
              <Lock className="w-5 h-5 text-primary" strokeWidth={2.2} />
            </div>

            <h2 className="font-display text-xl font-bold tracking-tight text-foreground mb-2">
              {reason || "Login to continue"}
            </h2>
            <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
              You're exploring the demo. Sign in to use your own gym data and unlock all actions.
            </p>

            <div className="flex flex-col gap-2" data-testid="login-modal-google">
              <button
                type="button"
                onClick={handleGoogle}
                disabled={busy}
                data-testid="login-modal-continue-with-google"
                className="w-full inline-flex items-center justify-center gap-3 bg-foreground text-background hover:opacity-90 font-medium px-5 py-2.5 rounded-lg transition-opacity disabled:opacity-60"
              >
                <svg className="w-4 h-4" viewBox="0 0 48 48" aria-hidden="true">
                  <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3l5.7-5.7C34.4 6.4 29.4 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"/>
                  <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.5 16.2 18.9 13 24 13c3.1 0 5.9 1.2 8 3l5.7-5.7C34.4 6.4 29.4 4 24 4 16.3 4 9.6 8.3 6.3 14.7z"/>
                  <path fill="#4CAF50" d="M24 44c5.3 0 10.1-2 13.7-5.3l-6.3-5.3C29.5 35 26.9 36 24 36c-5.3 0-9.7-3.3-11.3-8l-6.6 5.1C9.5 39.6 16.2 44 24 44z"/>
                  <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.4l6.3 5.3c-.4.4 6.4-4.7 6.4-14.2 0-1.3-.1-2.6-.4-4z"/>
                </svg>
                {busy ? "Redirecting…" : "Continue with Google"}
              </button>
              <button
                type="button"
                onClick={handleEmailRoute}
                data-testid="login-modal-email"
                className="w-full text-xs text-muted-foreground hover:text-foreground py-2 transition-colors"
              >
                Use email & password instead →
              </button>
            </div>

            <p className="mt-4 text-[11px] text-center text-muted-foreground/70">
              Your gym workspace is created automatically on first sign-in.
            </p>
          </div>
        </div>
      )}
    </LoginPromptContext.Provider>
  );
};
